<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import {
  COMPUTE_TASK_TYPES,
  VmLifecycleType,
  VM_LIFECYCLES,
  VmLifecycleSelectOptions,
} from '@/constants';
import { highlightJson } from '@/utils/prism-utils';
import {
  BButton,
  BCollapse,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
} from 'bootstrap-vue-next';

// --- Component properties ---
const props = defineProps<{
  size?: string;
  computeTaskType?: string;
}>();

// --- Component state ---
const rootEl = ref<HTMLElement | null>(null);
const size = props.size ? props.size : 'md'; // Default size if not provided

const expanded = ref(false);
const jsonParamsPreviewExpanded = ref(false);
const version = ref<string>('');
const vmLifecycle = ref<VmLifecycleType>(VM_LIFECYCLES.AUTO);
const machineType = ref<string>('');

// Compatibility values read by the Nextflow form; legacy controls are retired.
const dataPipelineMin = ref<boolean>(false);
const dataPipelineCopyData = ref<boolean>(false);

// ATTRIBUTES FROM JSON
const jsonParams = ref<string>(''); // string representation of a JSON object with additional parameters
const jsonParamsValidation = computed(() => {
  const rawValue = jsonParams.value.trim();
  if (!rawValue) {
    return {
      error: '',
      isValid: null as boolean | null,
      value: null as Record<string, unknown> | null,
    };
  }

  try {
    const parsedValue = JSON.parse(rawValue) as unknown;

    if (
      parsedValue === null ||
      Array.isArray(parsedValue) ||
      typeof parsedValue !== 'object'
    ) {
      return {
        error: 'Enter a valid JSON object.',
        isValid: false,
        value: null,
      };
    }

    return {
      error: '',
      isValid: true,
      value: parsedValue as Record<string, unknown>,
    };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error.message : 'Enter a valid JSON object.',
      isValid: false,
      value: null,
    };
  }
});

const extraAttributes = computed<Record<string, unknown> | null>(
  () => jsonParamsValidation.value.value
);
const extraAttributesInvalid = computed(
  () => jsonParamsValidation.value.isValid === false
);

const resizeJsonParamsTextarea = async () => {
  await nextTick();
  const textarea =
    rootEl.value?.querySelector<HTMLTextAreaElement>('#json-params');

  if (!textarea) {
    return;
  }

  textarea.style.height = 'auto';
  textarea.style.height = `${textarea.scrollHeight}px`;
};

const formatJsonParamsIfValid = async () => {
  if (!extraAttributes.value) {
    return;
  }

  const formattedValue = JSON.stringify(extraAttributes.value, null, 2);
  if (formattedValue !== jsonParams.value) {
    jsonParams.value = formattedValue;
  }

  await resizeJsonParamsTextarea();
};

watch(jsonParams, () => {
  resizeJsonParamsTextarea();
});

watch(expanded, (isExpanded) => {
  if (isExpanded) {
    resizeJsonParamsTextarea();
  }
});

// Methods to populate JSON params with sample data for easier testing
function jsonAdd_Pipeline_Optional() {
  jsonParams.value = JSON.stringify(
    {
      ...extraAttributes.value,
      startPhase: '',
      endPhase: '',
      bucketOutput: {
        name: '',
        path: '',
      },
    },
    null,
    2
  );
  resizeJsonParamsTextarea();
}

function jsonAdd_NextFlow_CustomWorkflow_Optional() {
  jsonParams.value = JSON.stringify(
    {
      ...extraAttributes.value,
      nfWorkflowSource: '',
      nfWorkflowProject: '',
      nfWorkflowRef: '',
      nfWorkflowPath: '',
      nfWorkflowEntrypoint: '',
      nfWorkflowBucket: '',
    },
    null,
    2
  );
  resizeJsonParamsTextarea();
}

function jsonAdd_NextFlow_General_Optional() {
  jsonParams.value = JSON.stringify(
    {
      ...extraAttributes.value,
      nfExecutor: '',
      nfInputBucket: '',
      nfInputPath: '',
      nfOutputBucket: '',
      nfOutputPath: '',
      nfParams: '', // string representation of a JSON object
      nfProfiles: '', // string representation of an array of strings, e.g. ["wgs_subset"]
      nfConfigFiles: '', // string representation of an array of strings, e.g. ["conf/aws.config"]
      nfResume: true,
      nfAnsiLog: true,
      nfWithTrace: true,
      nfWithReport: true,
      nfWithTimeline: true,
      nfWithDag: true,
      nfRunLabel: '',
      nfMaxRuntimeSecondsSet: false,
      nfMaxRuntimeSeconds: 1800,
      nfBucket: null, // optional developer override, e.g. "sh-nextflow-dev-us"
      nfGcpRegion: null, // optional legacy/developer override, e.g. "us-central1"
      nfAwsRegion: null, // optional legacy/developer override, e.g. "us-east-1"
      nfAwsQueue: null, // optional developer override, e.g. "arn:aws:batch:us-east-1:123456789012:job-queue/example"
      nfAwsS3WorkDir: null, // optional developer override, e.g. "s3://sh-nextflow-ml-use1/work"
    },
    null,
    2
  );
  resizeJsonParamsTextarea();
}

// MOUNTED -> AutoSize JSON Textarea
onMounted(() => {
  resizeJsonParamsTextarea();
});

// --- Exposed properties ---
defineExpose({
  expanded,
  version,
  vmLifecycle,
  machineType,
  // DATA PIPELINE specific options
  dataPipelineMin,
  dataPipelineCopyData,
  // Extra attributes from JSON
  extraAttributes,
  extraAttributesInvalid,
});
</script>
<template>
  <div ref="rootEl" :class="[expanded ? 'expanded-group' : 'collapsed-group']">
    <BButton
      variant="outline-dark"
      @click="expanded = !expanded"
      :size="size"
      class="w-100"
    >
      <font-awesome-icon :icon="['fas', 'code']" class="me-2" />
      Developer Options
      <font-awesome-icon
        :icon="['fas', expanded ? 'angle-up' : 'angle-down']"
        class="ms-2"
      />
    </BButton>
    <BCollapse v-model="expanded">
      <!-- GENERAL DEVELOPER OPTIONS -->
      <hr />
      <h5 class="mt-3">General Developer Options</h5>

      <!-- VM Lifecycle -->
      <b-form-group label="VM Lifecycle" class="mt-3">
        <b-form-select
          :options="VmLifecycleSelectOptions"
          v-model="vmLifecycle"
          name="vm-lifecycle"
          required
        />
      </b-form-group>

      <!-- Machine type -->
      <BFormGroup label="Machine type" label-for="machine-type" class="mt-3">
        <BFormInput
          v-model="machineType"
          id="machine-type"
          name="machine-type"
          placeholder="Enter machine type (e.g. f1-micro) or leave blank for default"
        ></BFormInput>
      </BFormGroup>

      <!-- ATTRIBUTES FROM JSON -->
      <hr />
      <h5 class="mt-3">Extra Attributes from JSON</h5>
      <p>
        <small class="text-muted">
          Add custom extra attributes if needed. String "null" will be parsed as
          null value, strings "true" and "false" as boolean, and numbers as
          integers or floats. If you need to pass strictly string values, use
          quotes around the value, e.g. "true", "123.45" (the quotes at the
          beginning and end of the string will be stripped away). Hover over the
          type if you want to see the final value.
        </small>
      </p>
      <BFormGroup
        label-for="nf-params"
        :invalid-feedback="jsonParamsValidation.error"
        :state="jsonParamsValidation.isValid"
      >
        <BFormTextarea
          v-model="jsonParams"
          id="json-params"
          name="json-params"
          placeholder='Enter JSON object with additional parameters, e.g. {"param1": "value1", "param2": 123}'
          rows="1"
          max-rows="20"
          :state="jsonParamsValidation.isValid"
          style="overflow: hidden; resize: none"
          @blur="formatJsonParamsIfValid"
          @input="resizeJsonParamsTextarea"
        ></BFormTextarea>
        <small class="text-muted">
          Valid JSON objects are automatically formatted with 2-space
          indentation.
          <span
            v-if="extraAttributes"
            class="text-muted"
            style="cursor: pointer"
            @click="jsonParamsPreviewExpanded = !jsonParamsPreviewExpanded"
          >
            Preview&nbsp;<font-awesome-icon
              :icon="[
                'fas',
                jsonParamsPreviewExpanded ? 'angle-up' : 'angle-down',
              ]"
              class="ms-1"
            />
          </span>
        </small>
      </BFormGroup>

      <div v-if="extraAttributes" class="mt-2">
        <BCollapse v-model="jsonParamsPreviewExpanded">
          <pre
            class="code-highlight mb-0"
            v-html="highlightJson(extraAttributes)"
          ></pre>
        </BCollapse>
      </div>

      <div>
        <!-- Add buttons to add custom attributes to JSON -->
        <BButton
          v-if="props.computeTaskType === COMPUTE_TASK_TYPES.NEXTFLOW_PIPELINE"
          variant="dark"
          class="me-2 mt-1"
          :size="size"
          @click="jsonAdd_Pipeline_Optional()"
        >
          <font-awesome-icon :icon="['fas', 'plus']" class="me-1" />
          Pipeline Optional Attributes
        </BButton>
        <BButton
          v-if="props.computeTaskType === COMPUTE_TASK_TYPES.NEXTFLOW_PIPELINE"
          variant="dark"
          class="me-2 mt-1"
          :size="size"
          @click="jsonAdd_NextFlow_CustomWorkflow_Optional()"
        >
          <font-awesome-icon :icon="['fas', 'plus']" class="me-1" />
          Custom NextFlow Workflow
        </BButton>
        <BButton
          v-if="props.computeTaskType === COMPUTE_TASK_TYPES.NEXTFLOW_PIPELINE"
          variant="dark"
          class="me-2 mt-1"
          :size="size"
          @click="jsonAdd_NextFlow_General_Optional()"
        >
          <font-awesome-icon :icon="['fas', 'plus']" class="me-1" />
          NextFlow Optional Attributes
        </BButton>
        <BButton
          variant="danger"
          class="me-2 mt-1"
          :size="size"
          @click="
            jsonParams = '';
            extraAttributes = null;
            resizeJsonParamsTextarea();
          "
        >
          <font-awesome-icon :icon="['fas', 'trash']" class="me-1" />
          Clear JSON attributes
        </BButton>
      </div>
    </BCollapse>
  </div>
</template>
